<template>
	<section
		v-if="showSection"
		class="teacher-recommend-section"
		:class="{ 'teacher-recommend-section--en': isEnglish }"
		:aria-label="t(`${I18N_NS}.teacherRecommend.ariaLabel`)"
		:aria-busy="showSkeleton"
	>
		<header class="teacher-recommend-section__header">
			<h2 class="teacher-recommend-section__title">{{ t(`${I18N_NS}.teacherRecommend.title`) }}</h2>
			<div class="teacher-recommend-section__filters">
				<div class="sort-select-wrapper">
					<select v-model="themeValue" class="sort-select" @change="handleThemeChange">
						<option v-for="option in themeOptions" :key="option.value" :value="option.value">
							{{ option.label }}
						</option>
					</select>
					<img class="sort-arrow" src="../../../pc/images/expand.png" alt="" />
				</div>
				<div class="sort-select-wrapper">
					<select v-model="sortValue" class="sort-select" @change="handleSortChange">
						<option v-for="option in sortOptions" :key="option.value" :value="option.value">
							{{ option.label }}
						</option>
					</select>
					<img class="sort-arrow" src="../../../pc/images/expand.png" alt="" />
				</div>
			</div>
		</header>

		<TeacherCardsSkeleton v-if="showSkeleton" variant="grid" :show-title="false" :count="4" />

		<template v-else>
			<div class="teacher-recommend-section__list">
				<article
					v-for="teacher in teacherList"
					:key="getTeacherUid(teacher)"
					:ref="el => setCardRef(el, teacher)"
					class="teacher-recommend-card"
					@click="handleCardClick(teacher)"
				>
					<div class="teacher-recommend-card__image-box">
						<img
							class="teacher-recommend-card__avatar"
							:src="teacher?.avatar"
							:alt="teacher?.name || t(`${I18N_NS}.teacherResource.avatarAlt`)"
						/>
					</div>

					<div class="teacher-recommend-card__content">
						<h3 class="teacher-recommend-card__name">{{ teacher?.nickname }}</h3>
						<p class="teacher-recommend-card__title">{{ teacher?.displayTitle }}</p>

						<div class="teacher-recommend-card__meta">
							<img :src="ratingStar" alt="" aria-hidden="true" />
							<span>{{ teacher?.displayScore }}({{ teacher?.displayConsultCount }})</span>
						</div>

						<div class="teacher-recommend-card__price">
							<img :src="priceIcon" alt="" aria-hidden="true" />
							<span>{{ teacher?.displayPrice }}</span>
							<span class="teacher-recommend-card__price-suffix">{{
								t(`${I18N_NS}.teacherRecommend.priceFrom`)
							}}</span>
						</div>

						<button
							class="teacher-recommend-card__button"
							type="button"
							@click.stop="handleCardAction(teacher)"
						>
							{{ teacher?.actionText }}
						</button>
					</div>
				</article>
			</div>

			<div v-if="hasMore" class="teacher-recommend-section__footer">
				<button class="teacher-recommend-section__more" type="button" @click="loadMore">
					{{
						loading
							? t(`${I18N_NS}.teacherResource.loading`)
							: t(`${I18N_NS}.teacherRecommend.loadMore`)
					}}
				</button>
			</div>
		</template>
	</section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { getHighServiceOriginTeacher } from "~/api/api";
import { useI18n } from "~/composables/useI18n";
import { useMasterFortuneTeacherNav } from "@variants/seer/master-fortune/composables/useMasterFortuneTeacherNav";
import {
	getMentorTypeByAreaType,
	getPriceBucket,
	normalizeMentorRating,
	useMentorListTracking,
} from "@variants/seer/master-fortune/composables/useMentorListTracking";
import ratingStar from "../../../images/rating-star.png";
import priceIcon from "../../../images/logo_icon.png";
import TeacherCardsSkeleton from "../teacher-cards-skeleton/index.vue";

const I18N_NS = "masterFortune";
const RESOURCE_SIZE = 8;
const DEFAULT_PAGE = 1;
const DEFAULT_PRICE = "495";
const TEACHER_DETAIL_AREA_TYPE = 10;
const STORAGE_KEY = "master_fortune_m_teacher_recommend_state";

const { t, locale, trackTranslationDeps } = useI18n();
const isEnglish = computed(() => locale?.value === "en");
const { track } = useMentorListTracking({
	deviceType: "mobile",
	mentorType: "normal",
});
const { toChat, toTeacherDetail } = useMasterFortuneTeacherNav();

const sortValue = ref("order_count");
const previousSortValue = ref("order_count");
const sortOptions = computed(() => {
	trackTranslationDeps();
	const sortKey = isEnglish.value
		? `${I18N_NS}.teacherRecommend.sortOptionsMobile`
		: `${I18N_NS}.teacherRecommend.sortOptions`;
	return [
		{ label: t(`${sortKey}.order_count`), value: "order_count" },
		{ label: t(`${sortKey}.buy_total`), value: "buy_total" },
	];
});

const themeValue = ref(1);
const themeOptions = computed(() => {
	trackTranslationDeps();
	return [
		{ label: t(`${I18N_NS}.teacherRecommend.themeOptions.1`), value: 1 },
		{ label: t(`${I18N_NS}.teacherRecommend.themeOptions.2`), value: 2 },
		{ label: t(`${I18N_NS}.teacherRecommend.themeOptions.3`), value: 3 },
		{ label: t(`${I18N_NS}.teacherRecommend.themeOptions.4`), value: 4 },
		{ label: t(`${I18N_NS}.teacherRecommend.themeOptions.5`), value: 5 },
	];
});

const rawTeacherListData = ref([]);
const currentPage = ref(DEFAULT_PAGE);
const lastPage = ref(DEFAULT_PAGE);
const loading = ref(true);
const cardRefs = new Map();
const impressedMentorIds = new Set();
let cardObserver = null;

const teacherList = computed(() => {
	trackTranslationDeps();
	return (rawTeacherListData.value || []).map(formatTeacherResource);
});
const hasMore = computed(() => currentPage.value < lastPage.value);
const showSkeleton = computed(() => loading.value && !teacherList.value.length);
const showSection = computed(() => loading.value || teacherList.value.length > 0);

function getTeacherUid(teacher) {
	return teacher?.teacher_uid || teacher?.answer_id || teacher?.id || "";
}

function isDetailOnlyTeacher(teacher) {
	return Number(teacher?.area_type) === TEACHER_DETAIL_AREA_TYPE;
}

function formatTeacherResource(teacher) {
	const detailOnly = isDetailOnlyTeacher(teacher);
	return {
		...teacher,
		teacher_uid: getTeacherUid(teacher),
		name: teacher?.nickname || teacher?.name || "",
		displayScore: formatScore(teacher?.score),
		displayConsultCount: formatConsultCount(teacher?.answers || teacher?.evaluate_num),
		displayTitle: teacher?.tags || "",
		displayPrice: formatPrice(
			teacher?.final_price ||
				teacher?.price ||
				teacher?.discount_price ||
				teacher?.unit_price ||
				teacher?.min_price
		),
		actionText: detailOnly
			? t(`${I18N_NS}.teacherRecommend.viewDetail`)
			: t(`${I18N_NS}.teacherRecommend.consultTeacher`),
	};
}

function formatScore(value) {
	const score = Number(value);
	if (!score) return "5.0";
	return Math.min(score, 5).toFixed(1);
}

function formatConsultCount(value) {
	const count = Number(value) || 0;
	return t(`${I18N_NS}.teacherRecommend.consultCount`, { count });
}

function formatPrice(value) {
	if (value === undefined || value === null || value === "") return DEFAULT_PRICE;
	if (typeof value === "number") return String(value);
	const priceText = String(value).replace(/\s+/g, "").trim();
	if (!priceText) return DEFAULT_PRICE;
	return priceText.replace(/起$/, "");
}

async function fetchTeacherPage(page) {
	loading.value = true;
	try {
		const res = await getHighServiceOriginTeacher({
			page,
			size: RESOURCE_SIZE,
			v: 2,
			sort_type: sortValue.value,
			search_type: themeValue.value,
		});
		const list = res?.data?.list || [];
		const pager = res?.data?.pager || {};

		if (page === DEFAULT_PAGE) {
			rawTeacherListData.value = list;
		} else {
			rawTeacherListData.value = [...rawTeacherListData.value, ...list];
		}

		currentPage.value = Number(pager?.current_page || page);
		lastPage.value = Number(pager?.last_page || page);
		persistState();
	} catch (error) {
		console.error("getHighServiceOriginTeacher error:", error);
	} finally {
		loading.value = false;
	}
}

const handleSortChange = event => {
	event.target.blur();
	fetchTeacherPage(DEFAULT_PAGE);
	track("mentor_sort_click", {
		sort_type: sortValue.value,
		previous_sort_type: previousSortValue.value,
		result_count: teacherList.value.length,
	});
	previousSortValue.value = sortValue.value;
};

const handleThemeChange = event => {
	event.target.blur();
	fetchTeacherPage(DEFAULT_PAGE);
	track("mentor_filter_click", {
		filter_type: "theme",
		filter_value: themeValue.value,
		result_count: teacherList.value.length,
	});
};

async function loadMore() {
	if (!hasMore.value || loading.value) return;
	track("mentor_load_more_click", {
		page_index: currentPage.value + 1,
		result_count: teacherList.value.length,
		filter_type: "theme",
		sort_type: sortValue.value,
	});
	await fetchTeacherPage(currentPage.value + 1);
}

function handleCardAction(teacher) {
	const teacherUid = getTeacherUid(teacher);
	if (!teacherUid) return;
	if (isDetailOnlyTeacher(teacher)) {
		toTeacherDetail(teacherUid);
		return;
	}
	track("mentor_consult_click", {
		mentor_id: teacherUid,
		mentor_type: getMentorTypeByAreaType(teacher?.area_type),
		section_id: "hot_mentor",
		price_bucket: getPriceBucket(teacher?.displayPrice),
	});
	toChat(teacherUid);
}

function handleCardClick(teacher) {
	const teacherUid = getTeacherUid(teacher);
	if (!teacherUid) return;
	track("mentor_card_click", {
		mentor_id: teacherUid,
		mentor_type: getMentorTypeByAreaType(teacher?.area_type),
		position: teacherList.value.findIndex(item => getTeacherUid(item) === teacherUid) + 1,
		topic_tags: teacher?.displayTitle || "",
	});
	toTeacherDetail(teacherUid);
}

const persistState = () => {
	if (!import.meta.client) return;
	sessionStorage.setItem(
		STORAGE_KEY,
		JSON.stringify({
			sortValue: sortValue.value,
			themeValue: themeValue.value,
			currentPage: currentPage.value,
			lastPage: lastPage.value,
			teacherListData: rawTeacherListData.value,
		})
	);
};

const restoreState = () => {
	if (!import.meta.client) return false;
	const raw = sessionStorage.getItem(STORAGE_KEY);
	if (!raw) return false;
	try {
		const data = JSON.parse(raw);
		sortValue.value = data?.sortValue || "order_count";
		themeValue.value = Number(data?.themeValue || 1);
		currentPage.value = Number(data?.currentPage || DEFAULT_PAGE);
		lastPage.value = Number(data?.lastPage || DEFAULT_PAGE);
		rawTeacherListData.value = Array.isArray(data?.teacherListData) ? data.teacherListData : [];
		return rawTeacherListData.value.length > 0;
	} catch (error) {
		console.warn("恢复导师推荐状态失败:", error);
		return false;
	}
};

const setCardRef = (el, teacher) => {
	const id = getTeacherUid(teacher);
	if (!id) return;
	if (el) cardRefs.set(id, el);
	else cardRefs.delete(id);
};

function refreshCardObserver() {
	if (!import.meta.client) return;
	cardObserver?.disconnect();
	cardObserver = new IntersectionObserver(
		entries => {
			entries.forEach(entry => {
				const mentorId = entry.target.dataset.mentorId;
				if (!mentorId || impressedMentorIds.has(mentorId)) return;
				if (!entry.isIntersecting) return;
				const teacher = teacherList.value.find(item => getTeacherUid(item) === mentorId);
				if (!teacher) return;
				impressedMentorIds.add(mentorId);
				track("mentor_card_impression", {
					mentor_id: mentorId,
					mentor_type: getMentorTypeByAreaType(teacher?.area_type),
					position: teacherList.value.findIndex(item => getTeacherUid(item) === mentorId) + 1,
					topic_tags: teacher?.displayTitle || "",
					price_bucket: getPriceBucket(teacher?.displayPrice),
					rating: normalizeMentorRating(teacher?.displayScore),
				});
			});
		},
		{ threshold: [0.3] }
	);

	teacherList.value.forEach(teacher => {
		const mentorId = getTeacherUid(teacher);
		const el = cardRefs.get(mentorId);
		if (!el) return;
		el.dataset.mentorId = mentorId;
		cardObserver.observe(el);
	});
}

watch(
	() => teacherList.value.length,
	() => nextTick(refreshCardObserver)
);

onMounted(async () => {
	if (!restoreState()) {
		await fetchTeacherPage(DEFAULT_PAGE);
	} else {
		loading.value = false;
	}
	nextTick(refreshCardObserver);
});

onBeforeUnmount(() => {
	cardObserver?.disconnect();
});
</script>

<style lang="scss" scoped>
.teacher-recommend-section {
	padding: 0 32px;

	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 16px;
		margin-bottom: 24px;
	}

	&__title {
		margin: 0;
		flex: 0 0 auto;
		font-weight: 700;
		font-size: 40px;
		line-height: 50px;
		color: #333333;
		white-space: nowrap;
	}

	&__filters {
		display: flex;
		align-items: center;
		gap: 12px;
		flex: 0 0 auto;
		min-width: 0;
	}

	&--en &__header {
		flex-direction: column;
		align-items: stretch;
	}

	&--en &__title {
		white-space: normal;
	}

	&--en &__filters {
		align-items: stretch;
		width: 100%;
	}

	&--en .sort-select-wrapper {
		flex: 1;
		min-width: 0;

		.sort-select {
			width: 100%;
			min-width: 0;
			max-width: none;
			font-size: 20px;
		}
	}

	&__list {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 22px;
	}

	&__footer {
		display: flex;
		justify-content: center;
		margin: 40px 0 0;
	}

	&__more {
		min-width: 160px;
		height: 60px;
		padding: 6px 44px;
		border: 1px solid #dfdfdf;
		border-radius: 8px;
		background: #ffffff;
		font-size: 28px;
		line-height: 48px;
		color: #595959;
		cursor: pointer;

		&:hover {
			background: #f5f5f5;
		}
	}

	.sort-select-wrapper {
		position: relative;
		flex: 0 0 auto;
		display: flex;
		align-items: center;

		.sort-select {
			box-sizing: border-box;
			width: auto;
			min-width: 148px;
			max-width: 200px;
			padding: 8px 36px 8px 12px;
			background: #ffffff;
			border: 1px solid #e0e0e0;
			border-radius: 8px;
			font-size: 22px;
			line-height: 32px;
			color: #333333;
			cursor: pointer;
			appearance: none;
			-webkit-appearance: none;
			-moz-appearance: none;
		}

		.sort-arrow {
			position: absolute;
			right: 12px;
			width: 20px;
			height: 20px;
			pointer-events: none;
		}
	}
}

.teacher-recommend-card {
	display: flex;
	flex-direction: column;
	min-width: 0;
	background: transparent;
	border: none;
	border-radius: 0;
	overflow: visible;
	cursor: pointer;

	&__image-box {
		display: flex;
		align-items: flex-end;
		justify-content: center;
		height: 264px;
		background: #fffaea;
		padding: 10px 24px 0;
		border-radius: 16px 16px 0 0;
		overflow: hidden;
	}

	&__avatar {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__content {
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: 10px 16px 16px;
		background: #ffffff;
		border: 1px solid #efefef;
		border-top: none;
		border-radius: 0 0 16px 16px;
	}

	&__name {
		margin: 0;
		font-weight: 700;
		font-size: 28px;
		line-height: 40px;
		height: 40px;
		color: #333333;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	&__title {
		margin: 8px 0 0;
		font-size: 24px;
		line-height: 32px;
		height: 32px;
		color: #595959;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	&__meta {
		display: inline-flex;
		align-items: center;
		width: fit-content;
		max-width: 100%;
		gap: 4px;
		margin-top: 12px;
		padding: 4px 10px;
		background: #fdfae9;
		border-radius: 4px;
		font-size: 20px;
		line-height: 32px;
		color: #595959;

		img {
			width: 24px;
			height: 24px;
			flex-shrink: 0;
		}

		span {
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
		}
	}

	&__price {
		display: inline-flex;
		align-items: flex-end;
		gap: 4px;
		margin-top: 24px;
		font-weight: 700;
		font-size: 36px;
		line-height: 36px;
		color: #de2828;

		img {
			width: 24px;
			height: 24px;
			flex-shrink: 0;
		}
	}

	&__price-suffix {
		font-size: 22px;
		font-weight: 500;
		line-height: 1;
	}

	&__button {
		width: 100%;
		height: 60px;
		margin-top: 24px;
		border: 0;
		border-radius: 4px;
		background: #333333;
		font-weight: 400;
		font-size: 28px;
		line-height: 60px;
		color: #ffffff;
		cursor: pointer;
	}
}
</style>
